import { Box, Divider, Stack, Typography } from "@mui/material";
import { formatInteger } from "@shared";

import { DutyScheduleDetailResponse } from "@/api";
import { DutyScheduleStatusChip } from "@/pages/duty-schedule/components/status-chip/DutyScheduleStatusChip";
import { useTranslation } from "@/translations";

import { formatPeriod } from "./DutyScheduleKeyFigures.utils";

// The dividers cancel this padding with a negative margin so they run border to border.
const ContainerPadding = 2;

interface Props {
  dutySchedule: DutyScheduleDetailResponse;
}

/**
 * The key figures of a duty schedule. All of them are the values snapshotted when the calculation ran,
 * so they keep reporting that state no matter what changed in the configuration since.
 */
export function DutyScheduleKeyFigures({ dutySchedule }: Props) {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      columnGap={3}
      rowGap={2}
      divider={<Divider orientation="vertical" flexItem sx={{ my: -ContainerPadding }} />}
      sx={{
        p: ContainerPadding,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "fsw.blue-5",
      }}
    >
      <Figure label={t("dutySchedule.detail.keyFigures.period")}>
        <Typography variant="body1" fontWeight={700}>
          {formatPeriod(dutySchedule.periodStart, dutySchedule.periodEnd)}
        </Typography>
      </Figure>

      <Figure label={t("dutySchedule.columns.status")}>
        <DutyScheduleStatusChip status={dutySchedule.status} />
      </Figure>

      <Figure label={t("dutySchedule.columns.workingDays")}>
        <Count value={dutySchedule.workingDays} />
      </Figure>

      <Figure label={t("dutySchedule.columns.dutyCount")}>
        <Count value={dutySchedule.dutyCount} />
      </Figure>

      <Figure label={t("dutySchedule.columns.counselorCount")}>
        <Count value={dutySchedule.counselorCount} />
      </Figure>
    </Stack>
  );
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Box mt={0.5}>{children}</Box>
    </Box>
  );
}

/**
 * A key figure is only ever absent on a duty schedule that has never been calculated. `highlight`
 * marks a figure that needs the Supervisor's attention — a plan with gaps in it.
 */
function Count({ value, highlight = false }: { value: number | null; highlight?: boolean }) {
  const { t, i18n } = useTranslation();

  return (
    <Typography variant="body1" fontWeight={700} color={highlight ? "error.main" : undefined}>
      {value == null ? t("dutySchedule.detail.notCalculated") : formatInteger(value, i18n.language)}
    </Typography>
  );
}
